import { useMemo, useState, memo, useCallback, Ref } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FormTemplateQuestionFieldOptionsValidityHintEnum,
  Task,
  TaskCompletion,
  TaskCompletionFilterInputObject,
  TaskCompletionScopeNameEnum,
  TaskScopeNameEnum,
} from '@lib/graphql-common';
import { TableMultiselectHandle } from '@lib/components/ModalTableMultiselect/ModalTableMultiselect';
import { FormRefType } from '@lib/components/ReactHookForm/types';
import useTaskCompletionsQueryHook from '@lib/utils/fetch/hooks/useTaskCompletionsQueryHook';
import { GetUrlParams, PaginationParams } from '@lib/enums/urls';
import useTasksMultiselectQueryHook from '@lib/utils/fetch/hooks/useTasksMultiselectQueryHook';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import IconSurface from '@lib/components/IconSurface/IconSurface';
import { Colors } from '@lib/enums/colors';
import TaskCompletionsModal from '../TaskCompletionsModal/TaskCompletionsModal';
import TaskCompletionsHeaderRow from '../TaskCompletionsModal/TaskCompletionsHeaderRow';
import modalStyles from '../TaskCompletionsModal/TaskCompletionsModal.module.scss';
import { TaskCompletionsModalUrls } from '../TaskCompletionsModal/types';
import useFilterFields from './utils/getFilterFields';
import getCompletionsColumns from './utils/getCompletionsColumns';
import { createAdditionalFilters } from './utils/createAdditionalFilters';
import useTaskPickerState, {
  useAllTabSnapshot,
} from '../utils/useTaskPickerState';
import TaskPickerTable from '../utils/TaskPickerTable';

export type TaskCompletionsTableMultiselectProps = {
  /** Route builders for the read-only completion preview opened from a row. */
  urls: TaskCompletionsModalUrls;
  isOpen: boolean;
  onClose: () => void;
  onSelect: (values: string[]) => void;
  /**
   * Renders the picker body on its own, without the surrounding `Modal` and
   * its Cancel / Confirm buttons, for a host that owns that chrome — e.g. one
   * step of Intervention's `TaskAndCompletionsModal` wizard. The host drives
   * confirmation through `ref` and is told about it via `onSelectionConfirmed`;
   * `onClose` goes unused.
   */
  inline?: boolean;
  ref?: Ref<TableMultiselectHandle>;
  /** Inline mode only: fired once a `confirmSelection()` run has emitted `onSelect`. */
  onSelectionConfirmed?: () => void;
  formRef: FormRefType;
  fieldName?: string;
  /**
   * Pre-selected ids to use instead of the ones read off `formRef`'s
   * `fieldName` — for a host whose form value may not belong to the task
   * currently being picked. Intervention's `TaskAndCompletionsModal` passes
   * `[]` once step 1 has staged a new Task, so the previous task's
   * completions don't carry over into step 2 (they'd be invisible, since
   * both tab queries are scoped to `groupKey`, yet still get committed).
   */
  selectedIdsOverride?: string[];
  // The recurring task chain (Task.groupKey) whose completions are pickable.
  // Without one the picker has nothing to query and stays effectively empty.
  groupKey?: string;
  // Name of the `groupKey` task, shown as a chip in the completion preview's
  // header. Omit to leave the chip out.
  taskName?: string;
  /**
   * The `groupKey` task, for the Site / Site Area / Asset header above the
   * table. When omitted (e.g. the host only knows the task's groupKey and
   * name), the chain's current task is fetched by `groupKey` instead.
   */
  task?: Task;
  /**
   * Backend scope ALWAYS applied to both the All and Selected tab queries —
   * for a caller that needs to limit this picker to a fixed backend policy
   * rather than a user-editable filter. E.g. the Intervention form passes
   * `ELIGIBLE_FOR_INTERVENTION`, the same policy scope the create mutation
   * validates against, so the picker never offers a completion the save
   * would reject. Caller-specific, not a default, since another consumer may
   * legitimately want to show solved/covered completions too.
   */
  scope?: TaskCompletionScopeNameEnum;
  /**
   * Extra filters ALWAYS applied to both the All and Selected tab queries,
   * on top of whatever the user's own Status/Period filter row produces.
   */
  baseFilters?: TaskCompletionFilterInputObject[];
  /**
   * Defense-in-depth alongside `scope`: disables (and strips from the final
   * selection) any row whose `solvedAt` is set, in case a solved completion
   * still reaches the table — e.g. a stale/cached row from before it was
   * solved.
   */
  restrictToUnsolved?: boolean;
  /**
   * Status options to hide from the picker's own "Status" filter dropdown —
   * cosmetic only, for when `scope` already excludes them so offering them
   * would just be a dead end (e.g. the Intervention form excludes "Success"
   * since its scope only ever returns non-conform rows).
   */
  excludedStatusFilterOptions?: FormTemplateQuestionFieldOptionsValidityHintEnum[];
  /**
   * Renders single-select radio rows instead of checkboxes and swaps the
   * selection on click instead of appending to it. Pair with
   * `selectionLimit={1}`.
   */
  radioSelect?: boolean;
  selectionLimit?: number;
  /**
   * Drops the row "view" action and its read-only completion preview — for a
   * host whose users shouldn't open completion details from the picker (the
   * user app).
   */
  hideCompletionPreview?: boolean;
};

// A groupKey names one recurring chain; its latest occurrence stands for it.
const HEADER_TASK_SCOPE = { name: TaskScopeNameEnum.Current };
const HEADER_TASK_PAGINATION: PaginationParams = {
  [GetUrlParams.Page]: '1',
  [GetUrlParams.PerPage]: '1',
};

function TaskCompletionsTableMultiselect({
  urls,
  isOpen,
  onClose,
  onSelect,
  inline,
  ref,
  onSelectionConfirmed,
  formRef,
  fieldName = 'completions',
  selectedIdsOverride,
  groupKey,
  taskName,
  task: taskProp,
  scope,
  baseFilters,
  restrictToUnsolved,
  excludedStatusFilterOptions,
  radioSelect,
  selectionLimit,
  hideCompletionPreview,
}: TaskCompletionsTableMultiselectProps) {
  const { t } = useTranslation();
  const [viewingCompletion, setViewingCompletion] =
    useState<TaskCompletion | null>(null);

  const filterStateDefault = useMemo(() => ({}), []);

  // Header task — see the `task` prop doc.
  const taskGroupKeys = useMemo(
    () => (groupKey ? [groupKey] : undefined),
    [groupKey],
  );
  const { collection: fetchedTasks } = useTasksMultiselectQueryHook({
    groupKeys: taskGroupKeys,
    scope: HEADER_TASK_SCOPE,
    paginationParams: HEADER_TASK_PAGINATION,
    skip: !isOpen || !groupKey || !!taskProp,
    usePrevious: false,
  });
  const headerTask = taskProp ?? fetchedTasks[0];
  const headerTaskName = headerTask?.name || taskName;

  const pickerState = useTaskPickerState<
    TaskCompletion,
    TaskCompletionFilterInputObject
  >({
    isOpen,
    formRef,
    fieldName,
    selectedIdsOverride,
    filterStateDefault,
    selectionLimit,
    baseFilters,
    createAdditionalFilters,
  });
  const {
    activeTabState,
    additionalFilters,
    additionalFiltersSelected: baseFiltersSelected,
    clearFilter,
    filterState,
    filterStateSelected,
    idsForSelectedTabQuery,
    isFetchingAllIds,
    isSelectedTabActive,
    orderingState,
    orderingStateSelected,
    paginationState,
    paginationStateSelected,
    searchState,
    searchStateSelected,
    selectedIds: selectedCompletions,
    setIsFetchingAllIds,
    setSelectedIdsForQuery,
    shouldRunAllQuery,
  } = pickerState;

  // All tab query
  const {
    collection,
    loading: completionsLoading,
    firstLoading: completionsFirstLoading,
    totalCount,
    fetchIds,
  } = useTaskCompletionsQueryHook({
    groupKey: groupKey || '',
    scope,
    additionalFilters,
    paginationParams: {
      ...(orderingState?.[0] || {}),
      ...(paginationState?.[0] || {}),
    },
    orderingParams: orderingState?.[0] || {},
    skip: !shouldRunAllQuery || !groupKey,
  });

  const {
    collection: allTabCollection,
    totalCount: allTabTotalCount,
    firstLoading: allTabFirstLoading,
  } = useAllTabSnapshot(pickerState, {
    collection,
    totalCount,
    loading: completionsLoading,
    firstLoading: completionsFirstLoading,
  });

  const additionalFiltersSelected = useMemo(() => {
    // `id` supports an IN predicate on TaskCompletionFilterInputObject, so
    // (unlike Departments/Interventions) the Selected tab can fetch an
    // arbitrary id set directly — no known-items cache fallback needed.
    if (!idsForSelectedTabQuery.length) return baseFiltersSelected;
    return [
      ...baseFiltersSelected,
      { id: { predicate: { in: idsForSelectedTabQuery } } },
    ];
  }, [baseFiltersSelected, idsForSelectedTabQuery]);

  const {
    collection: collectionSelectedRaw,
    loading: completionsLoadingSelected,
    firstLoading: completionsFirstLoadingSelected,
    totalCount: totalCountSelectedRaw,
  } = useTaskCompletionsQueryHook({
    groupKey: groupKey || '',
    scope,
    additionalFilters: additionalFiltersSelected,
    paginationParams: {
      ...orderingStateSelected[0],
      ...paginationStateSelected[0],
    },
    orderingParams: orderingStateSelected[0],
    skip:
      !isOpen ||
      !isSelectedTabActive ||
      !groupKey ||
      idsForSelectedTabQuery.length === 0,
    fetchPolicy: 'cache-first',
  });

  const collectionSelected = useMemo(
    () => (idsForSelectedTabQuery.length === 0 ? [] : collectionSelectedRaw),
    [collectionSelectedRaw, idsForSelectedTabQuery.length],
  );
  const totalCountSelected =
    idsForSelectedTabQuery.length === 0 ? 0 : totalCountSelectedRaw;

  const onViewCompletion = useCallback((completion: TaskCompletion) => {
    setViewingCompletion(completion);
  }, []);

  const columns = getCompletionsColumns({
    t,
    onViewCompletion: hideCompletionPreview ? undefined : onViewCompletion,
  });
  const filterFormFields = useFilterFields({
    excludedValidityHints: excludedStatusFilterOptions,
  });

  // See `restrictToUnsolved` prop doc.
  const isCompletionSolved = useCallback(
    (completion: TaskCompletion) => !!completion.solvedAt,
    [],
  );
  const solvedCompletionIds = useMemo(() => {
    if (!restrictToUnsolved) return null;
    const ids = new Set<string>();
    [...allTabCollection, ...collectionSelected].forEach((item) => {
      if (isCompletionSolved(item)) ids.add(String(item.id));
    });
    return ids;
  }, [
    restrictToUnsolved,
    allTabCollection,
    collectionSelected,
    isCompletionSolved,
  ]);
  const handleSelect = useCallback(
    (values: string[]) => {
      onSelect(
        solvedCompletionIds
          ? values.filter((id) => !solvedCompletionIds.has(id))
          : values,
      );
    },
    [onSelect, solvedCompletionIds],
  );

  if (
    !activeTabState ||
    !searchState ||
    !paginationState ||
    !orderingState ||
    !filterState
  ) {
    return null;
  }

  const headerContent = headerTask && (
    <TaskCompletionsHeaderRow
      className={modalStyles.headerRowInPicker}
      task={headerTask}
      leadingItem={
        headerTaskName && (
          <Chip
            type={ChipTypes.chipGray}
            size={ChipSizes.small}
            leftIcon="task_alt"
            leftIconSymbolsOutlined
            label={headerTaskName}
          />
        )
      }
      urls={urls}
    />
  );

  // Shared by both the standalone-modal and the inline (host-owned chrome)
  // renderings — only the wrapper around the picker body differs.
  const tableProps = {
    activeTabState,
    columns,
    data: allTabCollection,
    dataSelected: collectionSelected,
    emptyStateTextKeys: ['no-task-reporting-yet', 'no-task-reporting-text'] as [
      string,
      string,
    ],
    fetchIds,
    filterFormFields,
    filterState,
    filterStateSelected,
    filterStateDefault,
    firstLoading: allTabFirstLoading,
    firstLoadingSelected: completionsFirstLoadingSelected,
    getIsRowDisabled: restrictToUnsolved ? isCompletionSolved : undefined,
    headerContent,
    hideSearch: true,
    isOpen,
    loading: shouldRunAllQuery ? completionsLoading : false,
    loadingSelected: completionsLoadingSelected || isFetchingAllIds,
    onIsFetchingAllIdsChange: setIsFetchingAllIds,
    onResetFilter: clearFilter,
    onSelect: handleSelect,
    onSelectedIdsChange: setSelectedIdsForQuery,
    orderingState,
    orderingStateSelected,
    paginationState,
    paginationStateSelected,
    radioSelect,
    searchPleceholder: t('completions-search-placeholder'),
    searchState,
    searchStateSelected,
    selected: selectedCompletions,
    selectionLimit,
    totalEntries: allTabTotalCount,
    totalEntriesSelected: totalCountSelected,
  };

  return (
    <>
      <TaskPickerTable<TaskCompletion>
        tableProps={tableProps}
        inline={inline}
        ref={ref}
        onSelectionConfirmed={onSelectionConfirmed}
        caption={t(
          selectionLimit === 1
            ? 'completion-select'
            : 'completions-multiselect',
        )}
        renderAfterCaption={
          <Tooltip body={t('select-completions-of-selected-task-hint')}>
            <IconSurface
              icon="info"
              symbolsOutlined
              color={Colors.Filter}
              size="sm"
            />
          </Tooltip>
        }
        onClose={onClose}
      />
      {!hideCompletionPreview && (
        <TaskCompletionsModal
          task={
            viewingCompletion
              ? ({
                  id: viewingCompletion.taskId,
                  groupKey,
                  name: headerTaskName,
                } as Task)
              : null
          }
          showTaskChip={!!headerTaskName}
          urls={urls}
          bucketId={null}
          initialCompletionId={viewingCompletion?.id}
          // The picker has no completions list behind it — see `detailOnly`.
          detailOnly
          onClose={() => setViewingCompletion(null)}
        />
      )}
    </>
  );
}

export default memo(TaskCompletionsTableMultiselect);
